import { createContext, useCallback, useEffect, useRef, useState } from 'react';
import ChannelTalkButton from '../components/ChannelTalkButton';
import * as ChannelService from '@channel.io/channel-web-sdk-loader';

import { errorToast } from '../utils/toast';

const ChannelTalkContext = createContext();

export const ChannelTalkProvider = ({ children }) => {
  const [isChannelLoaded, setIsChannelLoaded] = useState(false);
  const bootPromiseRef = useRef(null);
  const isLoadedRef = useRef(false); // cleanup용 ref (state 변경이 useEffect를 재실행시키지 않도록)

  const bootChannelTalk = useCallback(() => {
    if (bootPromiseRef.current) {
      return bootPromiseRef.current;
    }

    bootPromiseRef.current = new Promise((resolve, reject) => {
      ChannelService.loadScript();
      ChannelService.boot(
        {
          pluginKey: process.env.REACT_APP_CHANNEL_TALK_PLUGIN_KEY,
          appearance: 'system',
        },
        (err) => {
          if (err) {
            console.error('[ChannelTalk] boot error:', err);
            errorToast('채널톡 로드에 실패했습니다.');
            bootPromiseRef.current = null;
            reject(err);
            return;
          }
          isLoadedRef.current = true;
          setIsChannelLoaded(true);
          resolve();
        },
      );
    });

    return bootPromiseRef.current;
  }, []);

  const openChannelChat = useCallback(
    async (chatId) => {
      try {
        await bootChannelTalk();
        if (chatId) {
          ChannelService.openChat(chatId);
        } else {
          ChannelService.showMessenger();
        }
      } catch {
        errorToast('채널톡 열기에 실패했습니다.');
      }
    },
    [bootChannelTalk],
  );

  const updateUser = useCallback((profile = {}) => {
    ChannelService.updateUser({ profile });
  }, []);

  const track = useCallback((eventName, properties = {}) => {
    ChannelService.track(eventName, properties);
  }, []);

  const onShowMessenger = useCallback((callback) => {
    ChannelService.onShowMessenger(callback);
  }, []);

  const onHideMessenger = useCallback((callback) => {
    ChannelService.onHideMessenger(callback);
  }, []);

  useEffect(() => {
    bootChannelTalk().catch(() => {});

    return () => {
      if (!isLoadedRef.current) return;
      try {
        ChannelService.shutdown();
        bootPromiseRef.current = null;
        isLoadedRef.current = false;
      } catch {
        errorToast('채널톡 종료에 실패했습니다.');
      }
    };
  }, [bootChannelTalk]);

  return (
    <ChannelTalkContext.Provider
      value={{ isChannelLoaded, openChannelChat, updateUser, track, onShowMessenger, onHideMessenger }}
    >
      {children}
      <ChannelTalkButton />
    </ChannelTalkContext.Provider>
  );
};

export default ChannelTalkContext;
